function parseOptionalInt(value) {
  if (value === undefined || value === null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function clinicMetric(row, key) {
  switch (key) {
    case 'name': return row.name || row.clinicId || '';
    case 'userTotal': return Number(row.userTotal || 0);
    case 'licenseUsed': return Number(row.licenseUsed || 0);
    case 'monthlyFeeTotal': return Number(row.monthlyFeeTotal || 0);
    case 'patients': return Number(row.usage?.patients || 0);
    case 'records': return Number(row.usage?.records || 0);
    case 'storageBytes': return Number(row.usage?.storageBytes || 0);
    case 'updates': return Number(row.usage?.updates || 0);
    case 'newPatients': return Number(row.usage?.newPatients || 0);
    case 'contractCount': return Number(row.contractCount || 0);
    default: return 0;
  }
}

function userMetric(row, key) {
  switch (key) {
    case 'displayName': return row.displayName || row.email || row.uid || '';
    case 'email': return row.email || '';
    case 'role': return row.role || '';
    case 'clinicName': return row.clinicName || row.clinicId || '';
    case 'isActive': return row.isActive === false ? 0 : 1;
    default: return '';
  }
}

function contractMetric(row, key) {
  switch (key) {
    case 'licenseName': return row.licenseName || '';
    case 'displayName': return row.displayName || row.licenseName || '';
    case 'clinicName': return row.clinicName || row.clinicId || '';
    case 'status': return row.status || '';
    case 'monthlyPrice': return Number(row.monthlyPrice || 0);
    default: return '';
  }
}

export function sortAdminHubRows(rows, domain, sortKey, order) {
  const resolver = domain === 'users' ? userMetric
    : domain === 'contracts' ? contractMetric
      : clinicMetric;
  const dir = order === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = resolver(a, sortKey);
    const bv = resolver(b, sortKey);
    if (typeof av === 'string' || typeof bv === 'string') {
      return dir * String(av).localeCompare(String(bv), 'ja');
    }
    return dir * (Number(av) - Number(bv));
  });
}

export function filterClinicRows(rows, query = {}) {
  const search = String(query.search || '').trim().toLowerCase();
  return rows.filter((row) => {
    const label = `${row.name || ''} ${row.clinicId || ''}`.toLowerCase();
    if (search && !label.includes(search)) return false;
    if (query.demo === 'demo' && row.isDemo !== true) return false;
    if (query.demo === 'non-demo' && row.isDemo === true) return false;
    if (query.payment === 'registered' && row.hasPaymentInfo !== true) return false;
    if (query.payment === 'unregistered' && row.hasPaymentInfo === true) return false;
    if (query.stripe === 'linked' && row.stripeCustomerLinked !== true) return false;
    if (query.stripe === 'unlinked' && row.stripeCustomerLinked === true) return false;
    if (query.expiredLicense === 'yes' && row.hasExpiredLicense !== true) return false;
    if (query.expiredLicense === 'no' && row.hasExpiredLicense === true) return false;
    const licenseUsed = Number(row.licenseUsed || 0);
    if (query.licenseUsageBand === '0' && licenseUsed !== 0) return false;
    if (query.licenseUsageBand === '1' && licenseUsed !== 1) return false;
    if (query.licenseUsageBand === '2-3' && (licenseUsed < 2 || licenseUsed > 3)) return false;
    if (query.licenseUsageBand === '4plus' && licenseUsed < 4) return false;
    const patients = Number(row.usage?.patients || 0);
    if (query.usagePatientsBand === 'none' && patients > 0) return false;
    if (query.usagePatientsBand === '1-49' && (patients < 1 || patients >= 50)) return false;
    if (query.usagePatientsBand === '50-199' && (patients < 50 || patients >= 200)) return false;
    if (query.usagePatientsBand === '200plus' && patients < 200) return false;
    const minUsers = parseOptionalInt(query.minUsers);
    if (minUsers != null && Number(row.userTotal || 0) < minUsers) return false;
    const minLicenses = parseOptionalInt(query.minLicenses);
    if (minLicenses != null && licenseUsed < minLicenses) return false;
    const minPatients = parseOptionalInt(query.minPatients);
    if (minPatients != null && patients < minPatients) return false;
    return true;
  });
}

export function filterUserRows(rows, query = {}) {
  const search = String(query.search || '').trim().toLowerCase();
  return rows.filter((row) => {
    const label = `${row.displayName || ''} ${row.email || ''} ${row.clinicName || ''} ${row.role || ''}`.toLowerCase();
    if (search && !label.includes(search)) return false;
    if (query.role && query.role !== 'all' && String(row.role || '') !== query.role) return false;
    if (query.status === 'active' && row.isActive === false) return false;
    if (query.status === 'inactive' && row.isActive !== false) return false;
    return true;
  });
}

export function filterContractRows(rows, query = {}) {
  const search = String(query.search || '').trim().toLowerCase();
  return rows.filter((row) => {
    const label = `${row.displayName || ''} ${row.licenseName || ''} ${row.clinicName || ''} ${row.status || ''}`.toLowerCase();
    if (search && !label.includes(search)) return false;
    if (query.status && query.status !== 'all' && String(row.status || '') !== query.status) return false;
    if (query.licenseName && query.licenseName !== 'all' && String(row.licenseName || '') !== query.licenseName) return false;
    if (query.priceBand === 'free' && row.isFree !== true && Number(row.monthlyPrice || 0) > 0) return false;
    if (query.priceBand === 'paid' && (row.isFree === true || Number(row.monthlyPrice || 0) <= 0)) return false;
    if (query.priceRangeBand === 'under3000' && (row.isFree === true || Number(row.monthlyPrice || 0) <= 0 || Number(row.monthlyPrice || 0) >= 3000)) return false;
    if (query.priceRangeBand === '3000-9999' && (Number(row.monthlyPrice || 0) < 3000 || Number(row.monthlyPrice || 0) >= 10000)) return false;
    if (query.priceRangeBand === '10000plus' && Number(row.monthlyPrice || 0) < 10000) return false;
    if (query.feeCategory === 'trial' && Number(row.monthlyPriceTrial || 0) <= 0) return false;
    if (query.feeCategory === 'billable' && Number(row.monthlyPriceBillable || 0) <= 0) return false;
    if (query.stripeSubscription === 'yes' && row.hasStripeSubscription !== true) return false;
    if (query.stripeSubscription === 'no' && row.hasStripeSubscription === true) return false;
    return true;
  });
}

export function applyDrilldownFilter(domain, rows, drilldownQuery) {
  if (domain === 'users') return filterUserRows(rows, drilldownQuery);
  if (domain === 'contracts') return filterContractRows(rows, drilldownQuery);
  return filterClinicRows(rows, drilldownQuery);
}

export const DRILLDOWN_STATE_INITIAL = {
  demoFilter: 'all',
  paymentFilter: 'all',
  roleFilter: 'all',
  statusFilter: 'all',
  licenseFilter: 'all',
  priceBand: 'all',
  priceRangeBand: 'all',
  userStatusFilter: 'all',
  stripeFilter: 'all',
  expiredLicenseFilter: 'all',
  licenseUsageBand: 'all',
  usagePatientsBand: 'all',
  stripeSubscriptionFilter: 'all',
  feeCategory: 'all',
};

export function drilldownStateToQuery(domain, drilldown) {
  return {
    demo: drilldown.demoFilter || 'all',
    payment: drilldown.paymentFilter || 'all',
    role: drilldown.roleFilter || 'all',
    status: domain === 'users' ? (drilldown.userStatusFilter || 'all') : (drilldown.statusFilter || 'all'),
    licenseName: drilldown.licenseFilter || 'all',
    priceBand: drilldown.priceBand || 'all',
    priceRangeBand: drilldown.priceRangeBand || 'all',
    stripe: drilldown.stripeFilter || 'all',
    expiredLicense: drilldown.expiredLicenseFilter || 'all',
    licenseUsageBand: drilldown.licenseUsageBand || 'all',
    usagePatientsBand: drilldown.usagePatientsBand || 'all',
    stripeSubscription: drilldown.stripeSubscriptionFilter || 'all',
    feeCategory: drilldown.feeCategory || 'all',
  };
}

export function buildToolbarApiParams({
  domain,
  targetDateKey,
  search,
  toolbarFilters,
  minUsers,
  minLicenses,
  minPatients,
}) {
  return {
    targetDateKey,
    domain,
    search,
    demo: toolbarFilters.demoFilter,
    payment: toolbarFilters.paymentFilter,
    role: toolbarFilters.roleFilter,
    status: domain === 'users' ? toolbarFilters.userStatusFilter : toolbarFilters.statusFilter,
    licenseName: toolbarFilters.licenseFilter,
    priceBand: toolbarFilters.priceBand,
    priceRangeBand: toolbarFilters.priceRangeBand,
    stripe: 'all',
    expiredLicense: 'all',
    licenseUsageBand: 'all',
    usagePatientsBand: 'all',
    stripeSubscription: 'all',
    feeCategory: 'all',
    minUsers,
    minLicenses,
    minPatients,
  };
}
